import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
} from "chart.js";
import { Line, Bar, Pie, Doughnut } from "react-chartjs-2";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {
  getBasicStats,
  getDailyTrends,
  getMonthlyStats,
  getSourceStats,
  getStatusStats
} from "@/actions/dashboard";
import {
  CalendarMinus2,
  ChartNoAxesCombined,
  CircleCheck,
  CircleX,
  Clock4,
  UsersRound,
  TrendingUp,
  TrendingDown,
  Filter
} from "lucide-react";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Define types
interface DateRange {
  startDate: Date;
  endDate: Date;
  key: string;
}

interface BasicStats {
  total_submissions: number;
  today_submissions: number;
  current_month_submissions: number;
  last_month_total_submissions: number;
  month_over_month_percentage: number;
  current_day_of_month: number;
  days_in_current_month: number;
  last_month_equivalent_submissions: number;
  projected_month_total: number;
  projected_change_percentage: number;
}

interface DailyTrendData {
  chartData: ChartData<"line">;
  stats: {
    totalSubmissions: number;
    avgSubmissionsPerDay: string;
    maxSubmissionDate: string;
    maxSubmissions: number;
  };
}

interface MonthlyStatsData {
  chartData: ChartData<"bar">;
  stats: {
    totalSubmissions: number;
    avgSubmissionsPerMonth: string;
    maxMonth: string;
    trend: "increasing" | "decreasing" | "stable";
  };
}

interface SourceData {
  pieChartData: ChartData<"pie">;
  sources: Array<{
    source_name: string;
    count: number;
    percentage: number;
    color: string;
  }>;
}

interface StatusData {
  doughnutChartData: ChartData<"doughnut">;
  funnelChartData: ChartData<"bar">;
  statuses: Array<{
    status_name: string;
    count: number;
    percentage: number;
    color: string;
  }>;
  funnelData: Array<{
    name: string;
    count: number;
    stage_order: number;
    color: string;
  }>;
  conversionRate: {
    overall: number;
    newLeads: number;
    converted: number;
    lost: number;
    dropoffRates: Array<{
      fromStage: string;
      toStage: string;
      conversionRate: number;
      dropoffRate: number;
      dropoffCount: number;
    }>;
  };
  lostData?: {
    name: string;
    count: number;
  };
}

const Dashboard: React.FC = () => {
  // State management
  const [basicStats, setBasicStats] = useState<BasicStats | null>(null);
  const [dailyTrends, setDailyTrends] = useState<DailyTrendData | null>(null);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStatsData | null>(null);
  const [sourceData, setSourceData] = useState<SourceData | null>(null);
  const [statusData, setStatusData] = useState<StatusData | null>(null);

  // Loading states
  const [loadingBasicStats, setLoadingBasicStats] = useState<boolean>(true);
  const [loadingDailyTrends, setLoadingDailyTrends] = useState<boolean>(true);
  const [loadingMonthlyStats, setLoadingMonthlyStats] = useState<boolean>(true);
  const [loadingSourceStats, setLoadingSourceStats] = useState<boolean>(true);
  const [loadingStatusStats, setLoadingStatusStats] = useState<boolean>(true);

  // Date range state
  const [dateRange, setDateRange] = useState<DateRange[]>([
    {
      startDate: moment().subtract(30, "days").toDate(),
      endDate: moment().toDate(),
      key: "selection",
    }
  ]);

  // State for showing date range picker
  const [showDateRangePicker, setShowDateRangePicker] = useState<boolean>(false);

  // Selector states
  const [monthlyPeriod, setMonthlyPeriod] = useState<string>("12");
  const [sourceMonth, setSourceMonth] = useState<string>("all");
  const [statusMonth, setStatusMonth] = useState<string>("all");

  // Effect to load basic stats on mount (only once)
  useEffect(() => {
    fetchBasicStats();
  }, []);

  // Effect to fetch daily trends when date range changes
  useEffect(() => {
    const startDate = moment(dateRange[0].startDate).format("YYYY-MM-DD");
    const endDate = moment(dateRange[0].endDate).format("YYYY-MM-DD");
    fetchDailyTrends(startDate, endDate);
  }, [dateRange]);

  // Effect to fetch monthly stats when period changes
  useEffect(() => {
    fetchMonthlyStats();
  }, [monthlyPeriod]);

  // Effect to fetch source stats when month changes
  useEffect(() => {
    fetchSourceStats();
  }, [sourceMonth]);

  // Effect to fetch status stats when month changes
  useEffect(() => {
    fetchStatusStats();
  }, [statusMonth]);

  const fetchBasicStats = async () => {
    setLoadingBasicStats(true);
    try {
      const { data } = await getBasicStats();
      setBasicStats(data as BasicStats);
    } catch (error) {
      console.error("Error fetching basic stats:", error);
    } finally {
      setLoadingBasicStats(false);
    }
  };

  const fetchDailyTrends = async (startDate: string, endDate: string) => {
    setLoadingDailyTrends(true);
    try {
      const { data } = await getDailyTrends(startDate, endDate);
      setDailyTrends(data as DailyTrendData);
    } catch (error) {
      console.error("Error fetching daily trends:", error);
    } finally {
      setLoadingDailyTrends(false);
    }
  };

  const fetchMonthlyStats = async () => {
    setLoadingMonthlyStats(true);
    try {
      const { data } = await getMonthlyStats(monthlyPeriod);
      setMonthlyStats(data as MonthlyStatsData);
    } catch (error) {
      console.error("Error fetching monthly stats:", error);
    } finally {
      setLoadingMonthlyStats(false);
    }
  };

  const fetchSourceStats = async () => {
    setLoadingSourceStats(true);
    let url = ``;

    if (sourceMonth !== "all") {
      const [year, month] = sourceMonth.split("-");
      url += `?month=${month}&year=${year}`;
    }

    try {
      const { data } = await getSourceStats(url);
      setSourceData(data as SourceData);
    } catch (error) {
      console.error("Error fetching source stats:", error);
    } finally {
      setLoadingSourceStats(false);
    }
  };

  const fetchStatusStats = async () => {
    setLoadingStatusStats(true);
    let url = ``;

    if (statusMonth !== "all") {
      const [year, month] = statusMonth.split("-");
      url += `?month=${month}&year=${year}`;
    }

    try {
      const { data } = await getStatusStats(url);
      setStatusData(data as StatusData);
    } catch (error) {
      console.error("Error fetching status stats:", error);
    } finally {
      setLoadingStatusStats(false);
    }
  };

  // Helper functions
  const formatNumber = (number: number): string => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return "-";
    return moment(dateString).format("MMM D, YYYY");
  };

  // Generate month options for selectors
  const generateMonthOptions = () => {
    const options = [];

    // Add "All Time" option
    options.push(
      <option key="all" value="all">
        All Time
      </option>
    );

    // Add month options
    for (let i = 0; i < 24; i++) {
      const monthDate = moment().subtract(i, "months");
      const value = monthDate.format("YYYY-M");
      const text = monthDate.format("MMMM YYYY");

      options.push(
        <option key={value} value={value}>
          {text}
        </option>
      );
    }

    return options;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Sticky Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="px-6 py-3">
          <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-xs text-gray-500 italic mt-1">
            Note: All numbers represent unique leads (one per phone number)
          </p>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-6">
          <div className="">

          {/* Basic Stats Cards Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Total Leads Card */}
            <div className="background-container rounded-lg shadow p-4 border-l-4 border-brandColor">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm gray-text">Total Leads</div>
                  <div className="text-2xl font-bold">
                    {loadingBasicStats ? (
                      <div className="animate-pulse h-8 w-20 rounded"></div>
                    ) : (
                      formatNumber(basicStats?.total_submissions || 0)
                    )}
                  </div>
                </div>
                <div className="text-brandColor">
                  <CalendarMinus2 className='h-7 w-7' />
                </div>
              </div>
            </div>

            {/* Today's Leads Card */}
            <div className="background-container rounded-lg shadow p-4 border-l-4 border-green-500">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm gray-text">
                    Today's Leads
                  </div>
                  <div className="text-2xl font-bold">
                    {loadingBasicStats ? (
                      <div className="animate-pulse h-8 w-20 rounded"></div>
                    ) : (
                      formatNumber(basicStats?.today_submissions || 0)
                    )}
                  </div>
                </div>
                <div className="text-green-500">
                  <Clock4 className="h-7 w-7" />
                </div>
              </div>
            </div>

            {/* Current Month Card */}
            <div className="background-container rounded-lg shadow p-4 border-l-4 border-brandColor">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm gray-text">Current Month</div>
                  <div className="text-2xl font-bold">
                    {loadingBasicStats ? (
                      <div className="animate-pulse h-8 w-20 rounded"></div>
                    ) : (
                      formatNumber(basicStats?.current_month_submissions || 0)
                    )}
                  </div>
                  {basicStats && (
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                      <div
                        className="bg-brandColor h-2.5 rounded-full"
                        style={{
                          width: `${Math.round(
                            (basicStats.current_day_of_month /
                              basicStats.days_in_current_month) *
                            100
                          )}%`,
                        }}
                      ></div>
                    </div>
                  )}
                </div>
                <div className="text-brandColor">
                  <CalendarMinus2 className='h-7 w-7' />
                </div>
              </div>
            </div>

            {/* Last Month Card */}
            <div className="background-container rounded-lg shadow p-4 border-l-4 border-yellow-500">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm gray-text">Last Month</div>
                  <div className="text-2xl font-bold">
                    {loadingBasicStats ? (
                      <div className="animate-pulse h-8 w-20 rounded"></div>
                    ) : (
                      formatNumber(
                        basicStats?.last_month_total_submissions || 0
                      )
                    )}
                  </div>
                  {basicStats && (
                    <span
                      className={`inline-block px-2 py-1 text-xs rounded mt-1 ${basicStats.month_over_month_percentage > 0
                        ? "bg-green-100 text-green-800"
                        : basicStats.month_over_month_percentage < 0
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                        }`}
                    >
                      {basicStats.month_over_month_percentage > 0 ? "+" : ""}
                      {basicStats.month_over_month_percentage.toFixed(1)}%
                    </span>
                  )}
                </div>
                <div className="text-yellow-500">
                  <ChartNoAxesCombined className='h-7 w-7' />
                </div>
              </div>
            </div>
          </div>

          {/* First Row - Monthly Projection & Monthly Leads */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Monthly Projection - 6 columns */}
            <div className="lg:col-span-6 background-container rounded-lg shadow">
              <div className="border-b border-gray-200 px-4 py-4">
                <h3 className="text-lg gray-text font-semibold">
                  Monthly Projection
                </h3>
              </div>
              <div className="p-4">
                <div className="text-center mb-4">
                  <h6 className="text-sm text-gray-600 mb-2">
                    Projected Total for This Month
                  </h6>
                  <h3 className="text-2xl font-bold">
                    {loadingBasicStats ? (
                      <div className="animate-pulse bg-gray-200 h-6 w-20 mx-auto rounded"></div>
                    ) : (
                      formatNumber(basicStats?.projected_month_total || 0)
                    )}
                  </h3>
                  {basicStats && (
                    <span
                      className={`inline-block px-2 py-1 text-xs rounded mt-1 ${basicStats.projected_change_percentage > 0
                        ? "bg-green-100 text-green-800"
                        : basicStats.projected_change_percentage < 0
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                        }`}
                    >
                      {basicStats.projected_change_percentage > 0
                        ? "+"
                        : ""}
                      {basicStats.projected_change_percentage.toFixed(1)}%
                    </span>
                  )}
                  <small className="block text-gray-500 mt-1">
                    compared to last month
                  </small>
                </div>
                <div className="h-48">
                  {basicStats && (
                    <Bar
                      data={{
                        labels: [
                          "Last Month",
                          "Current (Actual)",
                          "Current (Projected)",
                        ],
                        datasets: [
                          {
                            label: "Leads",
                            data: [
                              basicStats.last_month_total_submissions,
                              basicStats.current_month_submissions,
                              basicStats.projected_month_total,
                            ],
                            backgroundColor: [
                              "rgba(156, 39, 176, 0.7)",
                              "rgba(225, 29, 72, 0.7)",
                              "rgba(76, 175, 80, 0.7)",
                            ],
                            borderColor: [
                              "rgba(156, 39, 176, 1)",
                              "rgba(225, 29, 72, 1)",
                              "rgba(76, 175, 80, 1)",
                            ],
                            borderWidth: 1,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false,
                          },
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                          },
                        },
                      }}
                    />
                  )}
                </div>
                <div className="text-gray-500 text-xs text-center mt-2">
                  <em>
                    Updated as of {moment().format("MMM D, YYYY")}
                  </em>
                </div>
              </div>
            </div>

            {/* Monthly Leads - 6 columns */}
            <div className="lg:col-span-6 background-container rounded-lg shadow">
              <div className="border-b border-gray-200 px-4 py-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold gray-text">
                  Monthly Leads
                </h3>
                <select
                  className="form-select text-sm border border-gray-300 rounded-md p-2 background-container"
                  value={monthlyPeriod}
                  onChange={(e) => setMonthlyPeriod(e.target.value)}
                >
                  <option value="12">Last 12 Months</option>
                  <option value="6">Last 6 Months</option>
                  <option value="24">Last 24 Months</option>
                  <option value="36">Last 36 Months</option>
                </select>
              </div>
              <div className="p-4">
                {loadingMonthlyStats ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
                  </div>
                ) : (
                  <>
                    <div className="h-48">
                      {monthlyStats && (
                        <Bar
                          data={monthlyStats.chartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: false,
                              },
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  precision: 0,
                                },
                              },
                            },
                          }}
                        />
                      )}
                    </div>

                    {monthlyStats && (
                      <div className="grid grid-cols-2 gap-3 mt-4">
                        <div className="bg-white rounded-lg shadow p-3 border-l-4 border-brandColor">
                          <div className="text-xs font-medium text-gray-600 mb-1">
                            Total Leads
                          </div>
                          <div className="text-lg font-bold text-gray-900">
                            {formatNumber(
                              monthlyStats.stats.totalSubmissions
                            )}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-3 border-l-4 border-green-500">
                          <div className="text-xs font-medium text-gray-600 mb-1">
                            Monthly Average
                          </div>
                          <div className="text-lg font-bold text-gray-900">
                            {monthlyStats.stats.avgSubmissionsPerMonth}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-3 border-l-4 border-purple-500">
                          <div className="text-xs font-medium text-gray-600 mb-1">
                            Peak Month
                          </div>
                          <div className="text-lg font-bold text-gray-900">
                            {monthlyStats.stats.maxMonth}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-3 border-l-4 border-yellow-500">
                          <div className="text-xs font-medium text-gray-600 mb-1">
                            Overall Trend
                          </div>
                          <div className="text-lg font-bold">
                            {monthlyStats.stats.trend ===
                              "increasing" && (
                                <span className="text-green-500 flex items-center">
                                  <TrendingUp className="h-4 w-4 mr-1" />
                                  <span className="text-sm">Increasing</span>
                                </span>
                              )}
                            {monthlyStats.stats.trend ===
                              "decreasing" && (
                                <span className="text-red-500 flex items-center">
                                  <TrendingDown className="h-4 w-4 mr-1" />
                                  <span className="text-sm">Decreasing</span>
                                </span>
                              )}
                            {monthlyStats.stats.trend === "stable" && (
                              <span className="text-gray-500 flex items-center">
                                <Filter className="h-4 w-4 mr-1" />
                                <span className="text-sm">Stable</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Second Row - Source Distribution & Daily Submission Trends */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Source Distribution */}
            <div className="background-container rounded-lg shadow">
              <div className="border-b border-gray-200 px-4 py-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold gray-text">
                  Source Distribution
                </h3>
                <select
                  className="background-container form-select text-sm border border-gray-300 rounded-md p-2"
                  value={sourceMonth}
                  onChange={(e) => setSourceMonth(e.target.value)}
                >
                  {generateMonthOptions()}
                </select>
              </div>
              <div className="p-4">
                {loadingSourceStats ? (
                  <div className="flex justify-center items-center h-48">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
                  </div>
                ) : (
                  <>
                    <div className="h-48">
                      {sourceData && (
                        <Pie
                          data={sourceData.pieChartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: "bottom",
                                labels: {
                                  boxWidth: 12,
                                  font: {
                                    size: 10,
                                  },
                                },
                              },
                            },
                          }}
                        />
                      )}
                    </div>

                    {sourceData && (
                      <div className="mt-4 space-y-2 max-h-32 overflow-y-auto">
                        {sourceData.sources.slice(0, 4).map((source, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center p-2 hover:bg-gray-50 rounded"
                          >
                            <div className="flex items-center">
                              <div
                                className="w-3 h-3 rounded-full mr-2"
                                style={{ backgroundColor: source.color }}
                              ></div>
                              <span className="text-sm">
                                {source.source_name}
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium">
                                {formatNumber(source.count)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {source.percentage.toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Daily Lead Trends */}
            <div className="background-container rounded-lg shadow">
              <div className="border-b border-gray-200 px-4 py-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold gray-text">
                  Daily Lead Trends
                </h3>
                <div className="relative">
                  <button
                    className="border border-gray-300 rounded-md p-1 text-xs inline-flex items-center"
                    onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                  >
                    {moment(dateRange[0].startDate).format("MMM D")} -{" "}
                    {moment(dateRange[0].endDate).format("MMM D")}
                    <Filter className="h-3 w-3 ml-1" />
                  </button>

                  {showDateRangePicker && (
                    <div className="absolute right-0 mt-2 z-10 shadow-lg rounded-md">
                      <DateRangePicker
                        ranges={dateRange}
                        onChange={(item) => {
                          setDateRange([item.selection as DateRange]);
                          setShowDateRangePicker(false);
                        }}
                        months={1}
                        direction="horizontal"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4">
                {loadingDailyTrends ? (
                  <div className="flex justify-center items-center h-48">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
                  </div>
                ) : (
                  <>
                    <div className="h-48">
                      {dailyTrends && (
                        <Line
                          data={dailyTrends.chartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: false,
                              },
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  precision: 0,
                                },
                              },
                            },
                          }}
                        />
                      )}
                    </div>

                    {dailyTrends && (
                      <div className="grid grid-cols-2 gap-3 mt-4">
                        <div className="text-center">
                          <div className="text-xs text-gray-500">
                            Total Leads
                          </div>
                          <div className="text-lg font-semibold">
                            {formatNumber(dailyTrends.stats.totalSubmissions)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">
                            Average Per Day
                          </div>
                          <div className="text-lg font-semibold">
                            {dailyTrends.stats.avgSubmissionsPerDay}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Highest Day</div>
                          <div className="text-sm font-semibold">
                            {formatDate(dailyTrends.stats.maxSubmissionDate)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">
                            Peak Leads
                          </div>
                          <div className="text-lg font-semibold">
                            {formatNumber(dailyTrends.stats.maxSubmissions)}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>


          {/* Conversion Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            <div className="lg:col-span-2">
              <div className="rounded-lg shadow background-container">
                <div className="border-b border-gray-200 px-4 py-4 flex justify-between items-center">
                  <h3 className="text-lg font-semibold gray-text">
                    Conversion Overview
                  </h3>
                  <select
                    className="form-select background-container text-sm border border-gray-300 rounded-md p-2"
                    value={statusMonth}
                    onChange={(e) => setStatusMonth(e.target.value)}
                  >
                    {generateMonthOptions()}
                  </select>
                </div>
                <div className="p-4">
                  <h4 className="text-lg font-semibold mb-2 gray-text">
                    Overall Conversion Rate
                  </h4>
                  {loadingStatusStats ? (
                    <div className="animate-pulse bg-gray-200 h-8 w-full rounded mb-4"></div>
                  ) : (
                    <div className="w-full bg-gray-200 rounded-full h-8 mb-4">
                      <div
                        className={`${statusData && statusData.conversionRate.overall < 10
                          ? "bg-red-500"
                          : statusData &&
                            statusData.conversionRate.overall < 25
                            ? "bg-yellow-500"
                            : "bg-green-500"
                          } h-8 rounded-full flex items-center justify-center text-white font-medium`}
                        style={{
                          width: `${statusData?.conversionRate.overall || 0}%`,
                        }}
                      >
                        {statusData?.conversionRate.overall || 0}%
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="background-container rounded-lg shadow border-l-4 border-brandColor p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs font-semibold text-brandColor uppercase mb-1">
                            New Leads
                          </div>
                          <div className="text-xl font-bold">
                            {loadingStatusStats ? (
                              <div className="animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
                            ) : (
                              formatNumber(
                                statusData?.conversionRate.newLeads || 0
                              )
                            )}
                          </div>
                        </div>
                        <div className="text-gray-400">
                          <UsersRound className="h-5 w-5" />
                        </div>
                      </div>
                    </div>

                    <div className="background-container rounded-lg shadow border-l-4 border-green-500 p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs font-semibold text-green-600 uppercase mb-1">
                            Converted
                          </div>
                          <div className="text-xl font-bold">
                            {loadingStatusStats ? (
                              <div className="animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
                            ) : (
                              formatNumber(
                                statusData?.conversionRate.converted || 0
                              )
                            )}
                          </div>
                        </div>
                        <div className="text-gray-400">
                          <CircleCheck className='w-5 h-5' />
                        </div>
                      </div>
                    </div>

                    <div className="background-container rounded-lg shadow border-l-4 border-red-500 p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs font-semibold text-red-600 uppercase mb-1">
                            Lost
                            {statusData &&
                              statusData.conversionRate.newLeads > 0 && (
                                <span className="text-xs text-gray-500 ml-1">
                                  (
                                  {(
                                    (statusData.conversionRate.lost /
                                      statusData.conversionRate.newLeads) *
                                    100
                                  ).toFixed(1)}
                                  %)
                                </span>
                              )}
                          </div>
                          <div className="text-xl font-bold">
                            {loadingStatusStats ? (
                              <div className="animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
                            ) : (
                              formatNumber(statusData?.conversionRate.lost || 0)
                            )}
                          </div>
                        </div>
                        <div className="text-gray-400">
                          <CircleX className='w-5 h-5' />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lead Funnel */}
            <div>
              <div className="background-container rounded-lg shadow">
                <div className="border-b border-gray-200 px-4 py-4">
                  <h3 className="text-lg font-semibold gray-text">
                    Lead Funnel
                  </h3>
                </div>
                <div className="p-4">
                  {loadingStatusStats ? (
                    <div className="flex justify-center items-center h-64">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  ) : (
                    <div className="funnel-wrapper">
                      {statusData &&
                        statusData.funnelData &&
                        statusData.funnelData
                          .sort((a, b) => a.stage_order - b.stage_order)
                          .map((item, index) => {
                            const totalCount = statusData.funnelData.reduce(
                              (sum, item) => sum + item.count,
                              0
                            );
                            const percentage = (
                              (item.count / totalCount) *
                              100
                            ).toFixed(1);
                            const width = Math.max(
                              40,
                              Math.min(
                                100,
                                100 -
                                (index *
                                  (100 / statusData.funnelData.length)) /
                                2
                              )
                            );

                            return (
                              <div
                                key={index}
                                className="flex justify-between items-center text-white rounded-md mb-2 p-3 shadow-sm"
                                style={{
                                  width: `${width}%`,
                                  backgroundColor: item.color,
                                }}
                              >
                                <div className="font-medium">{item.name}</div>
                                <div className="text-sm">
                                  {formatNumber(item.count)} ({percentage}%)
                                </div>
                              </div>
                            );
                          })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Lead Status Distribution */}
          <div className="background-container rounded-lg shadow mt-6">
            <div className="border-b border-gray-200 px-4 py-4">
              <h3 className="text-lg font-semibold gray-text">
                Lead Status Distribution
              </h3>
            </div>
            <div className="p-4">
              {loadingStatusStats ? (
                <div className="flex justify-center items-center h-96">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="h-96">
                      {statusData && (
                        <Doughnut
                          data={statusData.doughnutChartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            cutout: "65%",
                            plugins: {
                              legend: {
                                position: "right",
                                labels: {
                                  boxWidth: 12,
                                  font: {
                                    size: 10,
                                  },
                                },
                              },
                            },
                          }}
                        />
                      )}
                    </div>
                    <div className="h-96">
                      {statusData && (
                        <Bar
                          data={statusData.funnelChartData}
                          options={{
                            indexAxis: "y",
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              title: {
                                display: true,
                                text: "Lead Funnel",
                                font: {
                                  size: 14,
                                },
                              },
                              legend: {
                                display: false,
                              },
                            },
                            scales: {
                              x: {
                                beginAtZero: true,
                                ticks: {
                                  precision: 0,
                                },
                              },
                            },
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {statusData && (
                    <div className="mt-6">
                      <h4 className="text-lg font-semibold mb-4 gray-text">
                        Status Breakdown
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {statusData.statuses.map(
                          (status, index) =>
                            status.count > 0 && (
                              <div
                                key={index}
                                className="background-container rounded-lg shadow p-3 border-l-4"
                                style={{ borderLeftColor: status.color }}
                              >
                                <div className="flex justify-between items-center">
                                  <div
                                    className="font-medium"
                                    style={{ color: status.color }}
                                  >
                                    {status.status_name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {status.percentage.toFixed(1)}%
                                  </div>
                                </div>
                                <div className="text-xl font-bold mt-1">
                                  {formatNumber(status.count)}
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1 mt-2">
                                  <div
                                    className="h-1 rounded-full"
                                    style={{
                                      width: `${status.percentage}%`,
                                      backgroundColor: status.color,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            )
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Stage-by-Stage Analysis */}
          <div className="background-container rounded-lg shadow mt-6">
            <div className="border-b border-gray-200 px-4 py-4">
              <h3 className="text-lg font-semibold gray-text">
                Stage-by-Stage Analysis
              </h3>
            </div>
            <div className="p-4">
              {loadingStatusStats ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="h-80">
                      {statusData && statusData.conversionRate.dropoffRates && (
                        <Bar
                          data={{
                            labels: statusData.conversionRate.dropoffRates.map(
                              (rate) => `${rate.fromStage} → ${rate.toStage}`
                            ),
                            datasets: [
                              {
                                label: "Conversion Rate (%)",
                                data: statusData.conversionRate.dropoffRates.map(
                                  (rate) => rate.conversionRate
                                ),
                                backgroundColor: "rgba(75, 192, 192, 0.8)",
                                borderColor: "rgba(75, 192, 192, 1)",
                                borderWidth: 1,
                              },
                              {
                                label: "Dropoff Rate (%)",
                                data: statusData.conversionRate.dropoffRates.map(
                                  (rate) => rate.dropoffRate
                                ),
                                backgroundColor: "rgba(255, 99, 132, 0.8)",
                                borderColor: "rgba(255, 99, 132, 1)",
                                borderWidth: 1,
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                              y: {
                                beginAtZero: true,
                                max: 100,
                                title: {
                                  display: true,
                                  text: "Percentage (%)",
                                },
                              },
                            },
                            plugins: {
                              title: {
                                display: true,
                                text: "Stage-by-Stage Conversion Analysis",
                                font: {
                                  size: 14,
                                },
                              },
                            },
                          }}
                        />
                      )}
                    </div>

                    <div>
                      {statusData && statusData.conversionRate.dropoffRates && (
                        <div className="background-elements overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                            <thead className="bg-gray-200 dark:bg-gray-700">
                              <tr>
                                <th
                                  scope="col"
                                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-b border-r border-gray-300"
                                >
                                  From Stage
                                </th>
                                <th
                                  scope="col"
                                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-b border-r border-gray-300"
                                >
                                  To Stage
                                </th>
                                <th
                                  scope="col"
                                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-b border-r border-gray-300"
                                >
                                  Conversion Rate
                                </th>
                                <th
                                  scope="col"
                                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-b border-r border-gray-300"
                                >
                                  Dropoff Rate
                                </th>
                                <th
                                  scope="col"
                                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-b border-gray-300"
                                >
                                  Dropoff Count
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {statusData.conversionRate.dropoffRates.map(
                                (rate, index) => (
                                  <tr key={index} className="border-b border-gray-300">
                                    <td className="px-4 py-2 whitespace-nowrap text-sm border-r border-gray-300">
                                      {rate.fromStage}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm border-r border-gray-300">
                                      {rate.toStage}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap border-r border-gray-300">
                                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                        {rate.conversionRate.toFixed(1)}%
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap border-r border-gray-300">
                                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                        {rate.dropoffRate.toFixed(1)}%
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm">
                                      {formatNumber(rate.dropoffCount)}
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;